import { clamp, segmentBox } from './math.js';
export const ARENA = { width: 32, height: 28 };
export function makeMap(mode = 'gift') {
  let id=0;
  const walls = [];
  const add=(x,z,w,h,kind='ice')=>walls.push({id:`w${id++}`,x,z,w,h,kind,hp:kind==='crate'?3200:6000, maxHp:kind==='crate'?3200:6000});
  // Rotationally symmetric lanes, with a clear central contest area.
  for(const s of [-1,1]) {
    add(-7*s,-4*s,4,1.7); add(7*s,-7.5*s,1.8,4);
    add(3.5*s,-3.5*s,1.8,2.6,'gift'); add(-3.8*s,-9*s,3.5,1.4,'gift');
    add(11.5*s,-1.5*s,1.6,3.4); add(-10.5*s,-10*s,2,2,'gift');
  }
  if(mode==='showdown') {
    for(const [x,z] of [[-3,0],[3,0],[0,-4],[0,4],[-11,5],[11,-5],[-7,9],[7,-9]]) add(x,z,1.3,1.3,'crate');
  }
  const bushes = [];
  for(const s of [-1,1]) for(const [x,z,w,h] of [[-8,-1,4,2.2],[7,-4.7,3.7,2],[-1.8,-7,2.3,3],[11,7.5,3,4]]) bushes.push({x:x*s,z:z*s,w,h});
  return { ...ARENA, walls, bushes };
}
export function inBush(p, map) { return map.bushes.some(b=>Math.abs(p.x-b.x)<b.w/2 && Math.abs(p.z-b.z)<b.h/2); }
export function lineOfSight(a,b,map,padding=0) { return !map.walls.some(w=>w.hp>0 && segmentBox(a.x,a.z,b.x,b.z,w,padding)!==Infinity); }
export function canStand(x,z,r,map) {
  if(Math.abs(x)>map.width/2-r || Math.abs(z)>map.height/2-r) return false;
  return !map.walls.some(w=>w.hp>0 && Math.hypot(x-clamp(x,w.x-w.w/2,w.x+w.w/2),z-clamp(z,w.z-w.h/2,w.z+w.h/2))<r-.001);
}
/** Axis separation keeps a character sliding along walls rather than getting stuck. */
export function moveActor(p, dx, dz, map) {
  const n=Math.max(1,Math.ceil(Math.hypot(dx,dz)/.22));
  for(let i=0;i<n;i++) {
    const x=clamp(p.x+dx/n,-map.width/2+p.radius,map.width/2-p.radius);
    const z=clamp(p.z+dz/n,-map.height/2+p.radius,map.height/2-p.radius);
    if(canStand(x,p.z,p.radius,map)) p.x=x;
    if(canStand(p.x,z,p.radius,map)) p.z=z;
  }
}
export function spawnPoint(index, mode) {
  if(mode==='showdown') { const a=index*Math.PI/3+Math.PI/2; return {x:Math.cos(a)*11,z:Math.sin(a)*11}; }
  if(mode==='duel') return {x:0,z:index%2===0?11:-11};
  return {x:([-4,0,4][Math.floor(index/2)%3]), z:index%2===0?11.6:-11.6};
}
/** Bounded A* on a one-unit navigation grid. Recomputed by bots at most twice a second. */
export function pathfind(from, to, map, radius=.52) {
  const W=32,H=28;
  const cell=(x,z)=>[clamp(Math.floor(x+W/2),0,W-1),clamp(Math.floor(z+H/2),0,H-1)];
  const pos=(i)=>({x:i%W+.5-W/2,z:Math.floor(i/W)+.5-H/2});
  const [sx,sz]=cell(from.x,from.z), [ex,ez]=cell(to.x,to.z), start=sz*W+sx, end=ez*W+ex;
  const open=[start], cost=new Map([[start,0]]), parents=new Map(), closed=new Set();
  const heuristic=i=>Math.abs(i%W-ex)+Math.abs(Math.floor(i/W)-ez);
  let best=start;
  for(let loops=0;open.length && loops<650;loops++) {
    open.sort((a,b)=>(cost.get(a)+heuristic(a))-(cost.get(b)+heuristic(b)));
    const i=open.shift(); if(closed.has(i))continue; closed.add(i);
    if(heuristic(i)<heuristic(best))best=i;
    if(i===end){best=i;break;}
    const x=i%W,z=Math.floor(i/W);
    for(const [dx,dz] of [[1,0],[-1,0],[0,1],[0,-1]]) {
      const nx=x+dx,nz=z+dz;
      if(nx<0||nz<0||nx>=W||nz>=H)continue;
      const ni=nz*W+nx,p=pos(ni); if(closed.has(ni)||!canStand(p.x,p.z,radius,map))continue;
      const c=cost.get(i)+1;
      if(c<(cost.get(ni)??Infinity)){cost.set(ni,c);parents.set(ni,i);open.push(ni);}
    }
  }
  const result=[]; let cursor=best;
  while(cursor!==start && parents.has(cursor)){result.push(pos(cursor));cursor=parents.get(cursor);}
  return result.reverse();
}
